'use client'

import { useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { api } from '@/lib/api'
import { formatDT, getTodayDate } from '@/lib/utils'
import { ArrowLeft, Search, Plus, Minus, Trash2, ShoppingCart, AlertTriangle, CheckCircle, Scan } from 'lucide-react'
import Link from 'next/link'

function PrixInput({ item, onValidate }: { item: CartItem; onValidate: (id: string, prix: number) => void }) {
  const [val, setVal] = useState(item.prix.toString())

  // Sync si le prix change de l'extérieur
  useEffect(() => { setVal(item.prix.toString()) }, [item.prix])

  return (
    <div className="flex-1 relative">
      <input
        type="number"
        inputMode="decimal"
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => onValidate(item.article.id, parseFloat(val) || 0)}
        className={`w-full px-3 py-2 pr-8 rounded-xl border text-sm font-bold outline-none ${
          item.prix < item.article.prix_min
            ? 'border-red-400 bg-red-50 text-red-600'
            : 'border-gray-200 bg-gray-50 focus:border-indigo-400'
        }`}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">DT</span>
    </div>
  )
}

interface CartItem {
  article: any
  quantite: number
  prix: number
}

export default function VentePage() {
  const { session } = useApp()
  const router = useRouter()
  const searchRef = useRef<HTMLInputElement>(null)

  const [articles, setArticles] = useState<any[]>([])
  const [filtered, setFiltered] = useState<any[]>([])
  const [search, setSearch] = useState('')
  const [cart, setCart] = useState<CartItem[]>([])
  const [showCart, setShowCart] = useState(false)
  const [priceAlert, setPriceAlert] = useState<{ item: CartItem; newPrix: number } | null>(null)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    loadArticles()
  }, [session])

  useEffect(() => {
    if (!search.trim()) { setFiltered(articles.slice(0, 20)); return }
    const q = search.toLowerCase()
    setFiltered(articles.filter(a => a.name.toLowerCase().includes(q) || a.barcode?.includes(q)).slice(0, 20))
  }, [search, articles])

  async function loadArticles() {
    const data = await api.getArticles(session!.shop.id, { inStock: true })
    setArticles(data || [])
    setFiltered((data || []).slice(0, 20))
  }

  function addToCart(article: any) {
    setCart(prev => {
      const existing = prev.find(i => i.article.id === article.id)
      if (existing) {
        if (existing.quantite >= article.stock) return prev
        return prev.map(i => i.article.id === article.id ? { ...i, quantite: i.quantite + 1 } : i)
      }
      return [...prev, { article, quantite: 1, prix: article.prix_vente }]
    })
    setSearch('')
    searchRef.current?.focus()
  }

  function updateQty(id: string, delta: number) {
    setCart(prev => prev
      .map(i => i.article.id === id ? { ...i, quantite: Math.max(0, Math.min(i.article.stock, i.quantite + delta)) } : i)
      .filter(i => i.quantite > 0)
    )
  }

  function updatePrix(id: string, newPrix: number) {
    const item = cart.find(i => i.article.id === id)
    if (!item) return
    if (newPrix < item.article.prix_min) {
      setPriceAlert({ item: { ...item, prix: newPrix }, newPrix })
      return
    }
    setCart(prev => prev.map(i => i.article.id === id ? { ...i, prix: newPrix } : i))
  }

  function confirmPrixAlert() {
    if (!priceAlert) return
    setCart(prev => prev.map(i => i.article.id === priceAlert.item.article.id ? { ...i, prix: priceAlert.newPrix } : i))
    setPriceAlert(null)
  }

  const total = cart.reduce((s, i) => s + i.prix * i.quantite, 0)

  async function confirmVente() {
    if (!cart.length) return
    setSaving(true)
    await api.createVente({
      shop_id: session!.shop.id,
      user_id: session!.user.id,
      total,
      lignes: cart.map(i => ({
        article_id: i.article.id,
        quantite: i.quantite,
        prix_unitaire: i.prix,
        ras_mel_unitaire: i.article.ras_mel,
      })),
    })
    setCart([])
    setShowCart(false)
    setSaving(false)
    setDone(true)
    setTimeout(() => setDone(false), 2500)
    loadArticles()
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3 px-4 pt-12 pb-3">
          <Link href="/dashboard" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900 flex-1">Vente</h1>
          {cart.length > 0 && (
            <button onClick={() => setShowCart(true)} className="relative w-11 h-11 bg-indigo-600 rounded-2xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-white" />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs font-bold flex items-center justify-center">
                {cart.reduce((s, i) => s + i.quantite, 0)}
              </span>
            </button>
          )}
        </div>
        <div className="px-4 pb-3 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Chercher article..." autoFocus
              className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-2xl border border-gray-200 focus:outline-none focus:border-indigo-400 focus:bg-white" />
          </div>
          <button className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-600">
            <Scan className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Articles */}
      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
        {filtered.map(article => {
          const inCart = cart.find(i => i.article.id === article.id)
          return (
            <div key={article.id}
              className={`w-full bg-white rounded-2xl p-4 flex items-center gap-3 shadow-sm border-2 transition-all ${inCart ? 'border-indigo-400 bg-indigo-50' : 'border-gray-100'}`}>
              <button className="flex-1 text-left min-w-0" onClick={() => addToCart(article)}>
                <p className="font-semibold text-gray-800 truncate">{article.name}</p>
                <p className="text-sm text-gray-400">Stock: {article.stock} {article.unit}</p>
              </button>
              <div className="flex items-center gap-2 flex-shrink-0">
                {inCart ? (
                  <div className="flex items-center gap-2 bg-white rounded-xl border border-indigo-200 p-1">
                    <button onClick={() => updateQty(article.id, -1)}
                      className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center active:scale-90 text-indigo-700 font-bold text-lg">
                      −
                    </button>
                    <span className="w-8 text-center font-bold text-indigo-700">{inCart.quantite}</span>
                    <button onClick={() => updateQty(article.id, +1)}
                      className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center active:scale-90 text-white font-bold text-lg">
                      +
                    </button>
                  </div>
                ) : (
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{formatDT(article.prix_vente)}</p>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Total bar */}
      {cart.length > 0 && !showCart && (
        <div className="bg-white border-t border-gray-100 px-4 py-3 flex-shrink-0">
          <button onClick={() => setShowCart(true)}
            className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg active:scale-95 flex items-center justify-between px-5">
            <span>{cart.reduce((s, i) => s + i.quantite, 0)} articles</span>
            <span>{formatDT(total)}</span>
          </button>
        </div>
      )}

      {/* Cart */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
          <div className="bg-white shadow-sm flex-shrink-0">
            <div className="flex items-center gap-3 px-4 pt-12 pb-4">
              <button onClick={() => setShowCart(false)} className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></button>
              <h2 className="text-xl font-bold text-gray-900 flex-1">Panier</h2>
              <button onClick={() => { setCart([]); setShowCart(false) }} className="text-red-500 text-sm font-semibold">Vider</button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
            {cart.map(item => (
              <div key={item.article.id} className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <p className="font-semibold text-gray-800 flex-1 pr-2">{item.article.name}</p>
                  <button onClick={() => setCart(c => c.filter(i => i.article.id !== item.article.id))}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-1">
                    <button onClick={() => updateQty(item.article.id, -1)} className="w-8 h-8 bg-white rounded-lg shadow-sm flex items-center justify-center active:scale-90">
                      <Minus className="w-4 h-4 text-gray-600" />
                    </button>
                    <span className="w-8 text-center font-bold text-gray-800">{item.quantite}</span>
                    <button onClick={() => updateQty(item.article.id, 1)} className="w-8 h-8 bg-white rounded-lg shadow-sm flex items-center justify-center active:scale-90">
                      <Plus className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                  <PrixInput item={item} onValidate={updatePrix} />
                  <p className="font-bold text-gray-900 w-24 text-right text-sm">{formatDT(item.prix * item.quantite)}</p>
                </div>
                {item.prix < item.article.prix_min && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Sous le minimum ({formatDT(item.article.prix_min)})
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white border-t border-gray-100 px-4 py-4 flex-shrink-0">
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-600 font-medium">Total</span>
              <span className="text-2xl font-bold text-gray-900">{formatDT(total)}</span>
            </div>
            <button onClick={confirmVente} disabled={saving}
              className="w-full py-4 bg-green-500 text-white rounded-2xl font-bold text-xl active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CheckCircle className="w-6 h-6" /> Confirmer la vente</>}
            </button>
          </div>
        </div>
      )}

      {/* Price alert */}
      {priceAlert && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPriceAlert(null)} />
          <div className="relative bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-center text-gray-800 mb-1">⚠️ Prix trop bas!</h3>
            <p className="text-center text-gray-500 text-sm mb-4">
              Prix min: <strong>{formatDT(priceAlert.item.article.prix_min)}</strong><br />
              Tu veux vendre à: <strong className="text-red-500">{formatDT(priceAlert.newPrix)}</strong>
            </p>
            <div className="flex gap-3">
              <button onClick={() => setPriceAlert(null)} className="flex-1 py-3 bg-gray-100 rounded-2xl font-semibold text-gray-700">Annuler</button>
              <button onClick={confirmPrixAlert} className="flex-1 py-3 bg-red-500 text-white rounded-2xl font-semibold">Confirmer quand même</button>
            </div>
          </div>
        </div>
      )}

      {/* Success toast */}
      {done && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-green-500 text-white px-6 py-3 rounded-2xl shadow-xl flex items-center gap-2 font-semibold">
          <CheckCircle className="w-5 h-5" /> Vente enregistrée! ✅
        </div>
      )}
    </div>
  )
}
